import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { CompareProvider } from '@/contexts/CompareContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { Toaster } from '@/components/ui/sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HomePage from '@/pages/HomePage';
import PoliciesPage from '@/pages/PoliciesPage';
import ComparePage from '@/pages/ComparePage';
import PolicyDetailPage from '@/pages/PolicyDetailPage';
import LoginPage from '@/pages/LoginPage';
import AdminPage from '@/pages/AdminPage';
import MyPoliciesPage from '@/pages/MyPoliciesPage';
import AddPolicyPage from '@/pages/AddPolicyPage';
import UserPolicyDetailPage from '@/pages/UserPolicyDetailPage';
import CustomPageView from '@/pages/CustomPageView';
import LeadEnquiryPage from '@/pages/LeadEnquiryPage';
import LeadSubmittedPage from '@/pages/LeadSubmittedPage';
import ScrollToTop from '@/components/ScrollToTop';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SettingsProvider>
        <AuthProvider>
          <CompareProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Routes with Layout */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/policies" element={<Layout><PoliciesPage /></Layout>} />
            <Route path="/compare" element={<Layout><ComparePage /></Layout>} />
            <Route path="/policy/:id" element={<Layout><PolicyDetailPage /></Layout>} />
            <Route path="/get-quote" element={<Layout><LeadEnquiryPage /></Layout>} />
            <Route path="/lead-submitted" element={<Layout><LeadSubmittedPage /></Layout>} />
            
            {/* User Policies Routes - Protected */}
            <Route
              path="/my-policies"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MyPoliciesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-policy"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AddPolicyPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-policies/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserPolicyDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            {/* Protected Admin Route */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Layout>
                    <AdminPage />
                  </Layout>
                </AdminRoute>
              }
            />
            
            {/* Dynamic Custom Pages Route */}
            <Route path="/page/:slug" element={<Layout><CustomPageView /></Layout>} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </CompareProvider>
      </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
